import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import {
  S_TIME,
  S_DISTANCE,
  S_ALTITUDE,
  S_POWER,
  S_HR,
  S_CADENCE,
  niceStep,
  buildPoints,
  buildSegments,
  averageValue,
  valueAtX,
  smoothProfile
} from '../utils/telemetry';
import './ActivityChartModal.css';

// График показателей одной активности.
// Источник тот же, что и у сравнения участников на этапе:
// GET /api/users/:user_id/activities/:id/telemetry — телеметрия заезда.
//
// Отличие от сравнения: на одном поле рисуются не два участника, а несколько
// показателей одного заезда, у каждого своя шкала значений.
const METRICS = [
  {
    key: 'power',
    index: S_POWER,
    labelKey: 'results.replay_power',
    axisKey: 'results.compare_axis_power',
    color: '#667eea',
    fromZero: true
  },
  {
    key: 'heart_rate',
    index: S_HR,
    labelKey: 'results.replay_heart_rate',
    axisKey: 'results.compare_axis_heart_rate',
    color: '#e0625a',
    fromZero: false
  },
  {
    key: 'cadence',
    index: S_CADENCE,
    labelKey: 'results.compare_metric_cadence',
    axisKey: 'results.compare_axis_cadence',
    color: '#2f9e6f',
    fromZero: true
  }
];

// Ось X: дистанция в метрах или время от старта в секундах.
// tolerance — на сколько единиц оси можно промахнуться мимо последней точки,
// minRange — минимальная ширина выделяемого участка
const X_AXES = [
  {
    key: 'distance',
    index: S_DISTANCE,
    labelKey: 'activity_chart.x_distance',
    axisKey: 'results.compare_axis_distance',
    tolerance: 100,
    minRange: 50
  },
  {
    key: 'time',
    index: S_TIME,
    labelKey: 'activity_chart.x_time',
    axisKey: 'activity_chart.axis_time',
    tolerance: 15,
    minRange: 5
  }
];

const pad = (value) => String(value).padStart(2, '0');

// Время от старта: 8:05 или 1:12:30
const formatSeconds = (seconds, withSeconds = true) => {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (h > 0) return withSeconds ? `${h}:${pad(m)}:${pad(s)}` : `${h}:${pad(m)}`;

  return `${m}:${pad(s)}`;
};

// Деления оси времени ставим по круглым интервалам, а не по «каждые 437 секунд»
const TIME_STEPS = [10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800];

const timeStep = (span, count) => {
  const rough = span / count;

  return TIME_STEPS.find(step => step >= rough) || TIME_STEPS[TIME_STEPS.length - 1];
};

const ActivityChartModal = ({ isOpen, onClose, userId, activityId, activityTitle }) => {
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Показателей может быть выбрано несколько — они рисуются на одном поле
  const [metricKeys, setMetricKeys] = useState(['power']);
  // По какой оси раскладывать заезд: по дистанции или по времени
  const [xKey, setXKey] = useState('distance');
  // Усреднение значений: сглаженная линия вместо сырой телеметрии
  const [smoothed, setSmoothed] = useState(false);
  // Точка оси X, на которую наведен курсор (метры или секунды)
  const [cursorPos, setCursorPos] = useState(null);
  // Выделенный участок — график масштабируется по нему
  const [range, setRange] = useState(null);
  // Текущее выделение мышью/пальцем, пока кнопка не отпущена
  const [drag, setDrag] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  const svgRef = useRef(null);

  const xAxis = X_AXES.find(item => item.key === xKey) || X_AXES[0];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Загрузка телеметрии активности
  useEffect(() => {
    if (!isOpen || !userId || !activityId) return undefined;

    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setMetricKeys(['power']);
      setXKey('distance');
      setSmoothed(false);
      setCursorPos(null);
      setRange(null);
      setDrag(null);

      try {
        const response = await axios.get(`/api/users/${userId}/activities/${activityId}/telemetry`);
        if (!cancelled) setData(response.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || t('activity_chart.no_data'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
      setLoading(false);
    };
    // t намеренно не в зависимостях: функция перевода пересоздается на каждом
    // рендере провайдера и сбрасывала бы уже загруженные данные
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId, activityId, reloadToken]);

  const samples = useMemo(() => (data && data.samples) || [], [data]);

  // Рельеф раскладываем по той же оси, что и показатели: высота лежит в сэмплах.
  // Сглаживаем скользящим средним — в FIT высота шумит на метровом уровне
  const profile = useMemo(() => smoothProfile(
    samples
      .filter(sample => sample[S_ALTITUDE] !== null && sample[S_ALTITUDE] !== undefined)
      .map(sample => [sample[xAxis.index], sample[S_ALTITUDE]])
  ), [samples, xAxis.index]);

  // Закрытие по Esc: окно открывается поверх списка активностей
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Последний выбранный показатель не снимаем — иначе рисовать нечего
  const toggleMetric = (key) => {
    setMetricKeys((prev) => {
      if (prev.includes(key)) {
        return prev.length === 1 ? prev : prev.filter(item => item !== key);
      }

      // Порядок показателей всегда как в METRICS: от него зависят оси
      return METRICS.filter(metric => prev.includes(metric.key) || metric.key === key)
        .map(metric => metric.key);
    });
  };

  // Смена оси меняет единицы, поэтому выделенный участок и курсор сбрасываем:
  // «участок 5–12» в метрах и в секундах — это разные места заезда
  const selectXAxis = (key) => {
    if (key === xKey) return;

    setXKey(key);
    setRange(null);
    setCursorPos(null);
    setDrag(null);
  };

  const activeMetrics = METRICS.filter(metric => metricKeys.includes(metric.key));

  // Геометрия графика. Вторая ось значений занимает место справа
  const width = isMobile ? 380 : 720;
  const height = isMobile ? 320 : 380;
  const rightAxis = activeMetrics.length > 1;
  const margin = isMobile
    ? { top: 18, right: rightAxis ? 42 : 14, bottom: 46, left: 44 }
    : { top: 22, right: rightAxis ? 58 : 24, bottom: 50, left: 58 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const lastSample = samples.length ? samples[samples.length - 1] : null;
  const totalX = Math.max(
    (data && (xAxis.key === 'time' ? data.duration : data.total_distance)) || 0,
    lastSample ? lastSample[xAxis.index] : 0
  );

  // Видимый участок: весь заезд или выделенный отрезок
  const viewFrom = range ? range.from : 0;
  const viewTo = range ? range.to : totalX;
  const viewSpan = Math.max(1, viewTo - viewFrom);

  const xFor = (value) => margin.left + (plotWidth * (value - viewFrom)) / viewSpan;

  const inView = (value) => value >= viewFrom && value <= viewTo;

  // У каждого показателя своя шкала: мощность, пульс и каденс живут
  // в разных единицах и на общей оси были бы нечитаемы.
  // Диапазон считаем по видимому участку — при увеличении линии разворачиваются
  // во всю высоту поля
  const series = activeMetrics.map((metric) => {
    const points = buildPoints(samples, metric.index, smoothed, xAxis.index);
    const values = points
      .filter(([x, value]) => value !== null && inView(x))
      .map(([, value]) => value);

    const rawMin = values.length ? Math.min(...values) : 0;
    const rawMax = values.length ? Math.max(...values) : 1;
    const valueSpan = Math.max(1, rawMax - rawMin);
    const yMax = rawMax + valueSpan * 0.1;
    const yMin = metric.fromZero ? 0 : Math.max(0, rawMin - valueSpan * 0.1);

    const yFor = (value) => (
      margin.top + plotHeight - (plotHeight * (value - yMin)) / (yMax - yMin)
    );

    return {
      metric,
      points,
      segments: buildSegments(points),
      average: averageValue(points),
      hasData: values.length > 0,
      yMin,
      yMax,
      yFor
    };
  });

  // Деления оси Y считаем по показателю, чья ось сейчас нарисована
  const ticksFor = ({ yMin, yMax }) => {
    const step = niceStep((yMax - yMin) / 5);
    const ticks = [];

    for (let value = Math.ceil(yMin / step) * step; value <= yMax; value += step) {
      ticks.push(Math.round(value * 10) / 10);
    }

    return ticks;
  };

  const leftSeries = series[0] || null;
  const rightSeries = rightAxis ? series[1] : null;

  // Профиль высот — подложка в нижней части поля графика.
  // Высоты берем по видимому участку: на выделенном отрезке рельеф читается детальнее
  const profileHeight = plotHeight * 0.55;
  const visibleProfile = profile.filter(point => inView(point[0]));
  const altitudes = (visibleProfile.length > 1 ? visibleProfile : profile).map(point => point[1]);
  const minAltitude = altitudes.length ? Math.min(...altitudes) : 0;
  const maxAltitude = altitudes.length ? Math.max(...altitudes) : 1;
  const altitudeSpan = Math.max(1, maxAltitude - minAltitude);
  const yForAltitude = (altitude) => (
    margin.top + plotHeight - (profileHeight * (altitude - minAltitude)) / altitudeSpan
  );

  const profilePath = profile.length > 1
    ? `${profile.map(([x, altitude], index) => (
      `${index === 0 ? 'M' : 'L'} ${xFor(x)} ${yForAltitude(altitude)}`
    )).join(' ')} L ${xFor(profile[profile.length - 1][0])} ${margin.top + plotHeight} L ${xFor(profile[0][0])} ${margin.top + plotHeight} Z`
    : null;

  // Деления оси X: километры или круглые интервалы времени
  const xTicks = [];
  if (xAxis.key === 'distance') {
    const stepKm = niceStep((viewSpan / 1000) / (isMobile ? 4 : 7));

    for (let km = Math.ceil((viewFrom / 1000) / stepKm) * stepKm; km * 1000 <= viewTo; km += stepKm) {
      const rounded = Math.round(km * 100) / 100;
      xTicks.push({ value: rounded * 1000, label: String(rounded) });
    }
  } else {
    const step = timeStep(viewSpan, isMobile ? 4 : 7);
    // Секунды в подписях нужны только на мелком шаге: на получасовом они пустые
    const withSeconds = step % 60 !== 0;

    for (let seconds = Math.ceil(viewFrom / step) * step; seconds <= viewTo; seconds += step) {
      xTicks.push({ value: seconds, label: formatSeconds(seconds, withSeconds) });
    }
  }

  const formatX = (value) => (
    xAxis.key === 'distance'
      ? `${(value / 1000).toFixed(1)} ${t('results.compare_km')}`
      : formatSeconds(value)
  );

  const pathFor = (segment, yFor) => segment
    .map(([x, value], index) => `${index === 0 ? 'M' : 'L'} ${xFor(x)} ${yFor(value)}`)
    .join(' ');

  // Точка оси под указателем: работает и мышью, и пальцем.
  // За краями поля прижимаем к границе видимого участка — иначе выделение
  // обрывается, стоит увести курсор с графика
  const positionAt = (clientX) => {
    const svg = svgRef.current;
    if (!svg || totalX <= 0) return null;

    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return null;

    const x = ((clientX - rect.left) / rect.width) * width;
    const ratio = Math.min(1, Math.max(0, (x - margin.left) / plotWidth));

    return viewFrom + ratio * viewSpan;
  };

  const handlePointerMove = (clientX) => {
    const position = positionAt(clientX);
    if (position === null) return;

    setCursorPos(position);
    if (drag) setDrag(prev => ({ ...prev, to: position }));
  };

  const startDrag = (clientX) => {
    const position = positionAt(clientX);
    if (position === null) return;

    setCursorPos(position);
    setDrag({ from: position, to: position });
  };

  // Минимальная ширина выделения — иначе обычный клик схлопнул бы график в точку
  const MIN_RANGE = Math.max(xAxis.minRange, viewSpan * 0.02);

  const finishDrag = () => {
    if (!drag) return;

    const from = Math.min(drag.from, drag.to);
    const to = Math.max(drag.from, drag.to);

    setDrag(null);
    if (to - from >= MIN_RANGE) setRange({ from, to });
  };

  const cursorX = cursorPos !== null ? xFor(cursorPos) : null;
  const dragFrom = drag ? Math.min(drag.from, drag.to) : null;
  const dragTo = drag ? Math.max(drag.from, drag.to) : null;

  // Подсказка и значения под курсором лежат в одной ячейке грида и оба всегда
  // отрисованы: скрытый вариант продолжает занимать место. Иначе смена подсказки
  // на значения меняла бы высоту окна, а вместе с ней прыгал бы и график
  const renderReadout = () => {
    const showValues = cursorPos !== null;

    return (
      <div className="activity-chart-footnote">
        <div className={`activity-chart-readout-hint${showValues ? ' activity-chart-footnote-hidden' : ''}`}>
          {t('activity_chart.hint')}
        </div>
        <div
          className={`activity-chart-readout${showValues ? '' : ' activity-chart-footnote-hidden'}`}
          aria-hidden={!showValues}
        >
          <span className="activity-chart-readout-distance">
            {formatX(showValues ? cursorPos : 0)}
          </span>
          {series.map(({ metric, points }) => {
            const value = showValues ? valueAtX(points, cursorPos, xAxis.tolerance) : null;

            return (
              <span key={`readout-${metric.key}`} className="activity-chart-readout-value">
                <span className="activity-chart-dot" style={{ background: metric.color }} />
                {t(metric.labelKey)}: <strong>{value === null ? '—' : `${value} ${t(metric.axisKey + '_unit')}`}</strong>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderChart = () => (
    <>
      <svg
        ref={svgRef}
        className="activity-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={t('activity_chart.title')}
        onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX); }}
        onMouseMove={(e) => handlePointerMove(e.clientX)}
        onMouseUp={finishDrag}
        onMouseLeave={() => { finishDrag(); setCursorPos(null); }}
        onTouchStart={(e) => startDrag(e.touches[0].clientX)}
        onTouchMove={(e) => handlePointerMove(e.touches[0].clientX)}
        onTouchEnd={finishDrag}
      >
        {/* Все, что рисуется по оси X, обрезаем полем графика:
            при выделенном участке точки за его границами уходят за оси */}
        <clipPath id="activity-chart-plot-clip">
          <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} />
        </clipPath>

        {/* Рельеф заезда — подложка под линиями показателей */}
        {profilePath && (
          <path className="activity-chart-profile" d={profilePath} clipPath="url(#activity-chart-plot-clip)" />
        )}

        {/* Сетка и подписи левой оси — по первому выбранному показателю */}
        {leftSeries && ticksFor(leftSeries).map(tick => (
          <g key={`y-left-${tick}`}>
            <line
              className="activity-chart-grid"
              x1={margin.left}
              x2={width - margin.right}
              y1={leftSeries.yFor(tick)}
              y2={leftSeries.yFor(tick)}
            />
            <text
              className="activity-chart-axis-label"
              x={margin.left - 8}
              y={leftSeries.yFor(tick) + 4}
              textAnchor="end"
              style={{ fill: leftSeries.metric.color }}
            >
              {tick}
            </text>
          </g>
        ))}

        {/* Вторая ось значений — справа, в цвете своего показателя */}
        {rightSeries && ticksFor(rightSeries).map(tick => (
          <text
            key={`y-right-${tick}`}
            className="activity-chart-axis-label"
            x={width - margin.right + 8}
            y={rightSeries.yFor(tick) + 4}
            textAnchor="start"
            style={{ fill: rightSeries.metric.color }}
          >
            {tick}
          </text>
        ))}

        {/* Подписи оси X: километры или время от старта */}
        {xTicks.map(tick => (
          <g key={`x-${tick.value}`}>
            <line
              className="activity-chart-grid activity-chart-grid-vertical"
              x1={xFor(tick.value)}
              x2={xFor(tick.value)}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
            <text
              className="activity-chart-axis-label"
              x={xFor(tick.value)}
              y={height - margin.bottom + 18}
              textAnchor="middle"
            >
              {tick.label}
            </text>
          </g>
        ))}

        <text
          className="activity-chart-axis-title"
          x={margin.left + plotWidth / 2}
          y={height - 10}
          textAnchor="middle"
        >
          {t(xAxis.axisKey)}
        </text>
        {leftSeries && (
          <text
            className="activity-chart-axis-title"
            x={-(margin.top + plotHeight / 2)}
            y={14}
            transform="rotate(-90)"
            textAnchor="middle"
            style={{ fill: leftSeries.metric.color }}
          >
            {t(leftSeries.metric.axisKey)}
          </text>
        )}
        {rightSeries && (
          <text
            className="activity-chart-axis-title"
            x={margin.top + plotHeight / 2}
            y={-(width - 14)}
            transform="rotate(90)"
            textAnchor="middle"
            style={{ fill: rightSeries.metric.color }}
          >
            {t(rightSeries.metric.axisKey)}
          </text>
        )}

        {/* Линии показателей */}
        {series.map(({ metric, segments, yFor }) => (
          <g key={`line-${metric.key}`} clipPath="url(#activity-chart-plot-clip)">
            {segments.map((segment, index) => (
              <path
                key={`segment-${metric.key}-${index}`}
                className="activity-chart-line"
                d={pathFor(segment, yFor)}
                stroke={metric.color}
              />
            ))}
          </g>
        ))}

        {/* Выделяемый участок */}
        {drag && dragTo > dragFrom && (
          <rect
            className="activity-chart-selection"
            x={xFor(dragFrom)}
            y={margin.top}
            width={Math.max(0, xFor(dragTo) - xFor(dragFrom))}
            height={plotHeight}
          />
        )}

        {/* Курсор по оси X */}
        {cursorX !== null && (
          <g clipPath="url(#activity-chart-plot-clip)">
            <line
              className="activity-chart-cursor"
              x1={cursorX}
              x2={cursorX}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
            {series.map(({ metric, points, yFor }) => {
              const value = valueAtX(points, cursorPos, xAxis.tolerance);
              if (value === null) return null;

              return (
                <circle
                  key={`cursor-${metric.key}`}
                  className="activity-chart-cursor-point"
                  cx={cursorX}
                  cy={yFor(value)}
                  r={isMobile ? 5 : 4.5}
                  fill={metric.color}
                />
              );
            })}
          </g>
        )}
      </svg>

      {renderReadout()}
    </>
  );

  const renderLegend = () => (
    <div className="activity-chart-legend">
      {series.map(({ metric, average, hasData }) => (
        <div key={`legend-${metric.key}`} className="activity-chart-legend-item">
          <span className="activity-chart-dot" style={{ background: metric.color }} />
          <div>
            <div className="activity-chart-legend-name">{t(metric.labelKey)}</div>
            <div className="activity-chart-legend-stats">
              {t('results.compare_average')}:{' '}
              <strong>
                {average === null || !hasData ? '—' : `${average} ${t(metric.axisKey + '_unit')}`}
              </strong>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderBody = () => {
    if (loading) return <div className="activity-chart-state">{t('common.loading')}</div>;

    if (error) {
      return (
        <div className="activity-chart-state">
          <div>{error}</div>
          <button className="activity-chart-retry" onClick={() => setReloadToken(prev => prev + 1)}>
            {t('results.replay_retry')}
          </button>
        </div>
      );
    }

    if (samples.length < 2) return <div className="activity-chart-state">{t('activity_chart.no_data')}</div>;

    return (
      <>
        <div className="activity-chart-controls">
          <div className="activity-chart-metrics">
            {METRICS.map(item => (
              <button
                key={item.key}
                type="button"
                className={`activity-chart-metric ${metricKeys.includes(item.key) ? 'activity-chart-metric-active' : ''}`}
                style={metricKeys.includes(item.key) ? { background: item.color, borderColor: item.color } : undefined}
                onClick={() => toggleMetric(item.key)}
                aria-pressed={metricKeys.includes(item.key)}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </div>

          <div className="activity-chart-x-switch">
            <span className="activity-chart-x-caption">{t('activity_chart.x_axis')}</span>
            <div className="activity-chart-x-options">
              {X_AXES.map(item => (
                <button
                  key={item.key}
                  type="button"
                  className={`activity-chart-x-option ${item.key === xAxis.key ? 'activity-chart-x-option-active' : ''}`}
                  onClick={() => selectXAxis(item.key)}
                  aria-pressed={item.key === xAxis.key}
                >
                  {t(item.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <label className="activity-chart-smooth">
            <input
              type="checkbox"
              checked={smoothed}
              onChange={(e) => setSmoothed(e.target.checked)}
            />
            <span>{t('results.compare_smooth')}</span>
          </label>
        </div>

        {range && (
          <div className="activity-chart-range">
            <span>
              {t(xAxis.key === 'distance' ? 'results.compare_range' : 'activity_chart.range_time')
                .replace('{from}', xAxis.key === 'distance' ? (range.from / 1000).toFixed(1) : formatSeconds(range.from))
                .replace('{to}', xAxis.key === 'distance' ? (range.to / 1000).toFixed(1) : formatSeconds(range.to))}
            </span>
            <button type="button" className="activity-chart-range-reset" onClick={() => setRange(null)}>
              {t('activity_chart.zoom_reset')}
            </button>
          </div>
        )}

        {renderLegend()}
        {renderChart()}
      </>
    );
  };

  return (
    // Окно открывается поверх модалки активностей — клик по подложке
    // не должен закрывать ее вместе с графиком
    <div
      className="activity-chart-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="activity-chart-modal" onClick={(e) => e.stopPropagation()}>
        <div className="activity-chart-header">
          <div>
            <h3 className="activity-chart-title">{t('activity_chart.title')}</h3>
            <div className="activity-chart-subtitle">{activityTitle}</div>
          </div>
          <button className="activity-chart-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="activity-chart-body">{renderBody()}</div>
      </div>
    </div>
  );
};

export default ActivityChartModal;
